import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildTradingPlatformAliasesPayload,
  mapTradingPlatformAliasesResponseToDto,
} from '../dto/system-trading-platforms.dto.js';
import { buildTradingPlatformAliasesRowsModel } from '../model/system-trading-platforms.model.js';

export function useSystemTradingPlatforms() {
  const { token } = useAuth();

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadPlatforms = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.getTradingPlatformAliases(token);
      const dto = mapTradingPlatformAliasesResponseToDto(response);
      setRows(buildTradingPlatformAliasesRowsModel(dto));
    } catch (requestError) {
      if (requestError?.status === 404) {
        setRows([]);
      } else {
        setRows([]);
        setError(requestError.message || 'Failed to load trading platforms');
      }
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPlatforms();
  }, [loadPlatforms]);

  const onAliasChange = useCallback((platform, value) => {
    setRows((prev) => prev.map((row) => (
      row.platform === platform
        ? { ...row, draftAlias: value }
        : row
    )));
  }, []);

  const saveAlias = useCallback(async (platform) => {
    if (isSaving) {
      return;
    }

    const currentRows = rows;
    const targetRow = currentRows.find((row) => row.platform === platform);
    if (!targetRow) {
      return;
    }

    const nextAlias = String(targetRow.draftAlias || '').trim();
    const currentAlias = String(targetRow.alias || '').trim();
    if (nextAlias === currentAlias) {
      return;
    }

    const payloadRows = buildTradingPlatformAliasesPayload(currentRows, platform, nextAlias);

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.updateTradingPlatformAliases(token, { platforms: payloadRows });
      const dto = mapTradingPlatformAliasesResponseToDto(response);
      const normalized = buildTradingPlatformAliasesRowsModel(dto);

      if (normalized.length > 0) {
        setRows(normalized);
      } else {
        setRows((prev) => prev.map((row) => (
          row.platform === platform
            ? { ...row, alias: nextAlias, draftAlias: nextAlias }
            : row
        )));
      }
      setSuccess('Trading platform aliases updated.');
    } catch (requestError) {
      setRows((prev) => prev.map((row) => (
        row.platform === platform
          ? { ...row, draftAlias: row.alias }
          : row
      )));
      setError(requestError.message || 'Failed to update trading platform alias');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, rows, token]);

  return {
    rows,
    isLoading,
    isSaving,
    error,
    success,
    onAliasChange,
    saveAlias,
  };
}

